import { isOutcomeEphemeral } from 'flow/service/outcomes/outcomes';
import { ActionType } from '../constants';
import type {
  Action,
  SchemaInstance,
  WorkflowProps,
} from '../types';
import {
  getActionPublishedAttributeToken,
  getTagActionsByTagName,
} from 'flow/service/actions';
import { isTagEphemeral } from 'flow/service/tags/tags';
import { getActionsByPublishedAttributeToken, isPublishedAttributeEphemeral } from 'flow/service/publishedAttributes';
import { getListActionsByListName, isListAction } from 'flow/service/actions/services/lists';
import { isListEphemeral } from 'flow/service/lists';
import {
  isCustomListEphemeral,
  isCustomListAction,
  getCustomListActionsByName,
} from 'flow/service/actions/services/customLists';
import { removeEphemeralOutcomeFromTags } from 'flow/store/redux/helpers';
import cloneDeep from 'lodash/cloneDeep';

/**
 * Removes all related action data from WorkflowProps.
 * Returns a new instance of workflowProps.
*/
export function pruneActionFromProps({
  action,
  workflowProps,
  workflowActions,
}: {
  action: Action;
  workflowProps: WorkflowProps;
  workflowActions: Partial<SchemaInstance>['actions'];
}) {
  const prunedProps = cloneDeep(workflowProps);

  switch (action.type) {
    case ActionType.TAG: {
      const { tag: { name } } = action;
      // check remaining actions to see if tag is used elsewhere
      const isTagInWorkflow = getTagActionsByTagName(
        workflowActions,
        name,
      ).filter(tagAction => action.publicId !== tagAction.publicId).length > 0;
      // if this action is only instance of tag in wf, remove from props
      if (!isTagInWorkflow) {
        // remove tag from props
        prunedProps.tags = prunedProps.tags.filter(
          tag => !(isTagEphemeral(tag) && tag.tag_name === name),
        );
      }
      break;
    }
    case ActionType.OUTCOME: {
      const { outcome: { name } } = action;
      /**
       * if used in any tags, this outcome must be removed
       * this means:
       * - all tags actions using this outcome need to be updated
       * - all connected tag props need to be updated
       */

      const { updatedActions, updatedTags } = removeEphemeralOutcomeFromTags({
        actions: workflowActions,
        tags: prunedProps.tags,
        outcomeName: name,
      });

      Object.assign(workflowActions, updatedActions);
      prunedProps.tags = updatedTags;
      prunedProps.outcomes = prunedProps.outcomes.filter(
        outcome => !(isOutcomeEphemeral(outcome) && outcome.outcome === name));

      break;
    }
    case ActionType.INPUT:
    case ActionType.OUTPUT: {
      const paToken = getActionPublishedAttributeToken(action);
      // check if PA is used elsewhere (can be used by both input + output in same WF)
      const connectedActions = getActionsByPublishedAttributeToken(workflowActions, paToken)
        .filter(publishedAction => action.publicId !== publishedAction.publicId);
      // only remove if actionToRemove is the only connected action
      if (connectedActions.length === 0) {
        prunedProps.publishedAttributes = prunedProps.publishedAttributes.filter(
          pa => !(isPublishedAttributeEphemeral(pa) && pa.attributeToken === paToken),
        );
      }

      break;
    }
    case ActionType.SERVICE: {
      if (isCustomListAction(action) || isListAction(action)) {
        const resourceName = action.service.name;
        const isList = isListAction(action);

        const otherActionsUsingList = (isList
          ? getListActionsByListName(workflowActions, resourceName)
          : getCustomListActionsByName(workflowActions, resourceName)
        ).filter(serviceAction => serviceAction.publicId !== action.publicId);

        if (otherActionsUsingList.length === 0) {
          if (isList) {
            prunedProps.lists = prunedProps.lists.filter(
              item => !(isListEphemeral(item) && item.list_name === resourceName),
            );
          } else {
            prunedProps.customLists = prunedProps.customLists.filter(
              item => !(isCustomListEphemeral(item) && item.name === resourceName),
            );
          }
        }
      }
      break;
    }
  }

  return prunedProps;
}
